import { getFileFromS3 } from "../../../../utils/helpers";
import { s3AutomationPrivate } from "../../../../utils/aws";

/** Last segment of S3 object key for UI labels (e.g. API `user_image_path`). */
export function getPetrifilmImageFileNameFromObjectKey(objectKey) {
  if (!objectKey || typeof objectKey !== "string") {
    return "";
  }
  const trimmed = objectKey.trim();
  if (!trimmed) {
    return "";
  }
  const lastSlash = trimmed.lastIndexOf("/");
  return lastSlash >= 0 ? trimmed.slice(lastSlash + 1) : trimmed;
}

function getExtensionFromFileName(fileName) {
  if (!fileName || typeof fileName !== "string") {
    return ".png";
  }
  const match = fileName.match(/(\.[a-z0-9]+)$/i);
  return match ? match[1] : ".png";
}

/**
 * S3 key: microscanner/{year}/{month}/{petrifilm_code}/{wo_id} {genista_id} {petrifilm_code} {df} {petrifilm}.{ext}
 */
export function buildMicroscannerS3ObjectKey({
  woId,
  genistaId,
  petrifilmCode,
  df,
  petrifilm,
  originalFileName,
}) {
  const extension = getExtensionFromFileName(originalFileName);
  const now = new Date();
  const year = String(now.getFullYear());
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const trimmedPetrifilmCode = petrifilmCode != null ? String(petrifilmCode).trim() : "";
  /** Folder uses truthy `petrifilmCode` so numeric `0` still maps to `_` (legacy behavior). */
  const codeFolder = (petrifilmCode && trimmedPetrifilmCode) || "_";
  const fileStem = [woId, genistaId, trimmedPetrifilmCode, df, petrifilm]
    .map((part) => (part == null ? "" : String(part).trim()))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  const filename = `${fileStem}${extension}`;
  return `microscanner/${year}/${month}/${codeFolder}/${filename}`;
}

/** First non-empty `user_image_path` on a sample row (e.g. after count submit). */
export function getFirstPetrifilmImagePathFromSamples(sampleRows) {
  if (!Array.isArray(sampleRows)) {
    return null;
  }
  const foundRow = sampleRows.find((row) => {
    const value = row.user_image_path;
    return typeof value === "string" && value.trim();
  });
  if (!foundRow) {
    return null;
  }
  return typeof foundRow.user_image_path === "string" ? foundRow.user_image_path.trim() : null;
}

/** True when the value is already usable as an `<img src>` without S3 presign (module-internal). */
function isDirectlyLoadableImageUrl(value) {
  if (!value || typeof value !== "string") {
    return false;
  }
  const trimmed = value.trim();
  return /^https?:\/\//i.test(trimmed) || trimmed.startsWith("data:") || trimmed.startsWith("blob:");
}

/**
 * Turn `micro_scanner` `user_image_path` (S3 key) into a display URL via presigned GET + blob URL,
 * or pass through http(s) / data / blob URLs unchanged.
 * @returns {Promise<{ previewUrl: string | null, revoke?: () => void }>}
 */
export async function resolvePetrifilmImagePreviewUrl(userImagePathOrUrl) {
  const raw = typeof userImagePathOrUrl === "string" ? userImagePathOrUrl.trim() : "";
  if (!raw) {
    return { previewUrl: null };
  }
  if (isDirectlyLoadableImageUrl(raw)) {
    return { previewUrl: raw };
  }
  const { success, blob } = await getFileFromS3(raw, s3AutomationPrivate);
  if (!success || !blob) {
    return { previewUrl: null };
  }
  const previewUrl = URL.createObjectURL(blob);
  return {
    previewUrl,
    revoke: () => URL.revokeObjectURL(previewUrl),
  };
}
